import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { InactivityService } from '../../services/inactivity.service';
import { SessionWarningModalComponent } from '../session-warning-modal/session-warning-modal.component';
import { SessionWarningData } from '../../interfaces/auth.interfaces';

/**
 * Componente que maneja la integración entre servicios de sesión y UI
 * Siguiendo principios SOLID:
 * - Single Responsibility: Solo maneja la coordinación entre sesión y UI
 * - Open/Closed: Extensible para nuevos tipos de advertencias
 * - Dependency Inversion: Depende de abstracciones de servicios
 */
@Component({
  selector: 'app-session-manager',
  template: `
    <app-session-warning-modal #sessionWarningModal></app-session-warning-modal>
  `,
  styleUrl: './session-manager.component.scss'
})
export class SessionManagerComponent implements OnInit, OnDestroy {
  
  @ViewChild('sessionWarningModal') sessionWarningModal!: SessionWarningModalComponent;
  
  private destroy$ = new Subject<void>();
  private isWarningActive = false;

  constructor(
    private authService: AuthService,
    private inactivityService: InactivityService
  ) {}

  ngOnInit(): void {
    this.setupSessionWarningHandling();
    console.log('SessionManager inicializado');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    console.log('SessionManager destruido');
  }

  /**
   * Configura el manejo de advertencias de sesión
   */
  private setupSessionWarningHandling(): void {
    // Suscribirse a advertencias de inactividad
    this.inactivityService.inactivityWarning$
      .pipe(takeUntil(this.destroy$))
      .subscribe(warningData => {
        this.handleInactivityWarning(warningData);
      });

    // Suscribirse a cambios en el estado de sesión
    this.authService.sessionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionState => {
        // Si el usuario se desautentica, ocultar cualquier modal activo
        if (!sessionState.isAuthenticated && this.isWarningActive) {
          this.hideSessionWarning();
        }
      });
  }

  /**
   * Maneja las advertencias de inactividad
   */
  private handleInactivityWarning(warningData: SessionWarningData): void {
    if (warningData.isSessionExpiring && warningData.remainingSeconds > 0) {
      // Mostrar advertencia
      if (!this.isWarningActive) {
        console.log('Mostrando modal de advertencia de sesión');
        this.showSessionWarning(warningData);
      } else {
        // Actualizar datos del modal existente
        if (this.sessionWarningModal) {
          this.sessionWarningModal.showWarning(warningData);
        }
      }
    } else {
      // Ocultar advertencia
      this.hideSessionWarning();
    }
  }

  /**
   * Muestra el modal de advertencia de sesión
   */
  private showSessionWarning(warningData: SessionWarningData): void {
    if (this.sessionWarningModal) {
      this.isWarningActive = true;
      this.sessionWarningModal.showWarning(warningData);
    } else {
      console.error('SessionWarningModal no está disponible');
    }
  }

  /**
   * Oculta el modal de advertencia de sesión
   */
  private hideSessionWarning(): void {
    if (this.isWarningActive && this.sessionWarningModal) {
      this.isWarningActive = false;
      this.sessionWarningModal.hideWarning();
    }
  }

  /**
   * Verifica si hay una advertencia activa
   */
  public hasActiveWarning(): boolean {
    return this.isWarningActive;
  }

  /**
   * Obtiene el estado del modal (útil para debugging)
   */
  public getModalState(): any {
    return this.sessionWarningModal ? this.sessionWarningModal.getState() : null;
  }
}
